import {
  EMPTY_ARRAY,
  UnifiedLiquidityEnum,
  useMarketAccountsContext,
  useMarketsContext,
  useSecuritiesContext,
} from '@talos/kyoko';
import { useMemo } from 'react';
import {
  getUnifiedLiquidityMarketTooltipGroupsFromEntityMarkets,
  getUnifiedLiquiditySymbols,
  type UnifiedLiquidityMarketTooltipGroup,
} from './unifiedLiquidityMarketTooltip';

interface EntityUnifiedLiquidityMarketTooltipEntity {
  Symbol?: string;
  Markets?: Parameters<typeof getUnifiedLiquidityMarketTooltipGroupsFromEntityMarkets>[0]['markets'];
  LegSummary?: Parameters<typeof getUnifiedLiquidityMarketTooltipGroupsFromEntityMarkets>[0]['legSummary'];
  Parameters?: {
    UnifiedLiquidity?: UnifiedLiquidityEnum;
  };
  unifiedLiquidity?: UnifiedLiquidityEnum;
}

export function useEntityUnifiedLiquidityMarketTooltipGroups(
  entity: EntityUnifiedLiquidityMarketTooltipEntity | undefined
): UnifiedLiquidityMarketTooltipGroup[] {
  const { marketAccountsByName } = useMarketAccountsContext();
  const { marketDisplayNameByName } = useMarketsContext();
  const { securitiesBySymbol } = useSecuritiesContext();

  return useMemo(
    () =>
      getUnifiedLiquidityMarketTooltipGroupsFromEntityMarkets({
        markets: entity?.Markets,
        legSummary: entity?.LegSummary,
        marketAccountsByName,
        marketDisplayNameByName,
        securitiesBySymbol,
      }),
    [entity?.LegSummary, entity?.Markets, marketAccountsByName, marketDisplayNameByName, securitiesBySymbol]
  );
}

export function isEntityUnifiedLiquidityEnabled(entity: EntityUnifiedLiquidityMarketTooltipEntity | undefined) {
  return (
    entity?.unifiedLiquidity === UnifiedLiquidityEnum.Enabled ||
    entity?.Parameters?.UnifiedLiquidity === UnifiedLiquidityEnum.Enabled
  );
}

export function shouldShowEntityUnifiedLiquidityMarketTooltip(
  entity: EntityUnifiedLiquidityMarketTooltipEntity | undefined,
  groups: UnifiedLiquidityMarketTooltipGroup[] = EMPTY_ARRAY
) {
  return (
    isEntityUnifiedLiquidityEnabled(entity) &&
    getUnifiedLiquiditySymbols(groups, { baseSymbol: entity?.Symbol }).length > 1
  );
}
